import {
  FLOWER_META,
  FLOWER_ORDER,
  GAME,
  UPGRADES,
  type CarryKind,
  type FlowerKind,
  type UpgradeId,
} from '../config';
import { costFor, effectPreview, type UpgradeLevels } from './Progression';

export class Hud {
  private readonly cashEl = this.el('#cash-value');
  private readonly goalEl = this.el('#goal-value');
  private readonly goalFill = this.el('#goal-fill');
  private readonly stackCount = this.el('#stack-count');
  private readonly stackMax = this.el('#stack-max');
  private readonly statusEl = this.el('#status-line');
  private readonly toastEl = this.el('#milestone-toast');
  private readonly tipEl = this.el('#tutorial-tip');
  private readonly strip = this.el('#resource-strip');
  private readonly floatLayer = this.el('#float-layer');
  private readonly hudRoot = this.el('#hud');
  private readonly titleScreen = this.el('#title-screen');
  private readonly upgradePanel = this.el('#upgrade-panel');
  private readonly upgradeList = this.el('#upgrade-list');
  private readonly upgradeCash = this.el('#upgrade-cash');

  private toastTimer = 0;
  private tipTimer = 0;
  private onBuy: ((id: UpgradeId) => void) | null = null;

  private onPanelToggle: ((open: boolean) => void) | null = null;

  constructor() {
    this.el('#upgrade-open').addEventListener('click', () => this.setUpgradeOpen(true));
    this.el('#upgrade-close').addEventListener('click', () => this.setUpgradeOpen(false));
    this.upgradePanel.addEventListener('click', (e) => {
      if (e.target === this.upgradePanel) this.setUpgradeOpen(false);
    });
  }

  setBuyHandler(fn: (id: UpgradeId) => void): void {
    this.onBuy = fn;
  }

  setPanelToggleHandler(fn: (open: boolean) => void): void {
    this.onPanelToggle = fn;
  }

  showTitle(show: boolean): void {
    this.titleScreen.classList.toggle('hidden', !show);
    this.hudRoot.classList.toggle('hidden', show);
    if (show) this.setUpgradeOpen(false);
  }

  setUpgradeOpen(open: boolean): void {
    this.upgradePanel.classList.toggle('hidden', !open);
    this.onPanelToggle?.(open);
  }

  isUpgradeOpen(): boolean {
    return !this.upgradePanel.classList.contains('hidden');
  }

  setCash(cash: number, goal: number, milestoneIndex: number): void {
    this.cashEl.textContent = String(Math.floor(cash));
    this.goalEl.textContent = String(goal);
    const prev = milestoneIndex <= 0 ? 0 : GAME.milestones[Math.min(milestoneIndex - 1, GAME.milestones.length - 1)]!;
    const span = Math.max(1, goal - prev);
    const pct = Math.min(100, Math.max(0, ((cash - prev) / span) * 100));
    this.goalFill.style.width = `${pct}%`;
  }

  setStack(count: number, max: number, counts: Record<CarryKind, number>): void {
    this.stackCount.textContent = String(count);
    this.stackMax.textContent = String(max);
    this.strip.innerHTML = '';
    const order: CarryKind[] = [...FLOWER_ORDER, 'honey', 'cash'];
    for (const kind of order) {
      const n = counts[kind] ?? 0;
      if (n <= 0) continue;
      const pill = document.createElement('div');
      pill.className = 'resource-pill';
      const color =
        kind === 'honey'
          ? '#f0b429'
          : kind === 'cash'
            ? '#3ecf7a'
            : `#${FLOWER_META[kind as FlowerKind].color.toString(16).padStart(6, '0')}`;
      pill.innerHTML = `<span class="dot" style="background:${color}"></span><span>${n}</span>`;
      this.strip.appendChild(pill);
    }
  }

  renderUpgrades(levels: UpgradeLevels, cash: number): void {
    this.upgradeCash.textContent = String(Math.floor(cash));
    this.upgradeList.innerHTML = '';
    for (const def of UPGRADES) {
      const level = levels[def.id];
      const maxed = level >= def.maxLevel;
      const cost = costFor(def.id, level);
      const row = document.createElement('div');
      row.className = 'upgrade-row';
      row.innerHTML = `
        <div>
          <h3>${def.label}</h3>
          <p>${def.blurb}</p>
        </div>
        <button type="button" class="buy-btn" data-id="${def.id}" ${maxed || cash < cost ? 'disabled' : ''}>
          ${maxed ? 'MAX' : `$${cost}`}
        </button>
        <div class="meta">
          <span>Lv ${level}/${def.maxLevel}</span>
          <span>${maxed ? def.effect : effectPreview(def.id, level)}</span>
        </div>
      `;
      const btn = row.querySelector('button')!;
      btn.addEventListener('click', () => this.onBuy?.(def.id));
      this.upgradeList.appendChild(row);
    }
  }

  setStatus(text: string): void {
    this.statusEl.textContent = text;
  }

  showMilestone(text: string, seconds = 2.6): void {
    this.toastEl.textContent = text;
    this.toastEl.classList.remove('hidden');
    this.toastTimer = seconds;
  }

  showTip(text: string, seconds = 4): void {
    this.tipEl.textContent = text;
    this.tipEl.classList.remove('hidden');
    this.tipTimer = seconds;
  }

  floatCash(amount: number): void {
    const el = document.createElement('div');
    el.className = 'float-cash';
    el.textContent = `+$${amount}`;
    this.floatLayer.appendChild(el);
    window.setTimeout(() => el.remove(), 950);
  }

  update(dt: number): void {
    if (this.toastTimer > 0) {
      this.toastTimer -= dt;
      if (this.toastTimer <= 0) this.toastEl.classList.add('hidden');
    }
    if (this.tipTimer > 0) {
      this.tipTimer -= dt;
      if (this.tipTimer <= 0) this.tipEl.classList.add('hidden');
    }
  }

  private el<T extends HTMLElement = HTMLElement>(sel: string): T {
    const node = document.querySelector<T>(sel);
    if (!node) throw new Error(`Missing HUD element ${sel}`);
    return node;
  }
}
